import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { IFaultCodeMapping } from '../../../../view/settings/api-automation-settings/fault-code-mapping/fault-code-mapping.model';
import { FaultCodeMappingService } from './fault-code-mapping.service';
import {
  ActionTypes,
  FaultCodeMappingsLoading,
  FaultCodeMappingsLoaded,
  FetchError,
  GetFaultCodeMapping,
  GetFaultCodeMappingCompleted,
  EditFaultCodeMapping,
  EditFaultCodeMappingCompleted,
  AddFaultCodeMapping,
  AddFaultCodeMappingCompleted,
  DeleteFaultCodeMappings,
  DeleteFaultCodeMappingsCompleted,
  ActivitiesDataLoading,
  ActivitiesDataCompleted,
  TasksDataLoading,
  TasksDataCompleted,
  TaskLookupDataLoading,
  TaskLookupDataCompleted,
} from './fault-code-mapping.actions';
import { ActivityService } from '../../../../shared/service/filter/activity.service';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { TaskService } from '../../../../shared/service/filter/task.service';

@Injectable()
export class FaultCodeMappingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: FaultCodeMappingService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly activityService: ActivityService,
    private readonly tasksService: TaskService,
    private readonly lookupService: ApiAutomationLookupService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.FAULT_CODE_MAPPINGS_DATA_LOADING),
      switchMap((payload: FaultCodeMappingsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [{ 'activity.name': { $cont: searchText } }, { 'task.title': { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new FaultCodeMappingsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_FAULT_CODE_MAPPING),
      switchMap((objectData: GetFaultCodeMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<IFaultCodeMapping>) =>
            of(new GetFaultCodeMappingCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_FAULT_CODE_MAPPING),
      switchMap((objectData: AddFaultCodeMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IFaultCodeMapping>) =>
            of(
              new AddFaultCodeMappingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_FAULT_CODE_MAPPING),
      switchMap((objectData: EditFaultCodeMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IFaultCodeMapping>) =>
            of(
              new EditFaultCodeMappingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_FAULT_CODE_MAPPINGS),
      switchMap((objectData: DeleteFaultCodeMappings) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteFaultCodeMappingsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getActivitiesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ACTIVITIES_DATA_LOADING),
      switchMap((objectData: ActivitiesDataLoading) =>
        from(
          this.activityService.getData(new HttpParams().set('s', JSON.stringify({ active: 1 })).set('limit', '1000')),
        ).pipe(
          switchMap((response) => of(new ActivitiesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getTasksData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.TASKS_DATA_LOADING),
      switchMap((objectData: TasksDataLoading) =>
        from(
          this.tasksService.getData(
            new HttpParams()
              .set('s', JSON.stringify({ statusId: 1, activityId: objectData.activityId }))
              .set('limit', '1000')
              .set('join', 'line||title'),
          ),
        ).pipe(
          switchMap((response) => of(new TasksDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getTaskLookupData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.TASK_LOOKUP_DATA_LOADING),
      switchMap((objectData: TaskLookupDataLoading) =>
        this.lookupService.getTaskOptions(objectData.taskIds).pipe(
          switchMap((response) => of(new TaskLookupDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
